import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Services', href: '#services' },
        { name: 'Projects', href: '#projects' },
        { name: 'About', href: '#about' },
        { name: 'Contact', href: '#contact' },
    ];

    return (
        <nav
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.85)' : 'transparent',
                backdropFilter: isScrolled ? 'blur(20px)' : 'none',
                WebkitBackdropFilter: isScrolled ? 'blur(20px)' : 'none',
                borderBottom: 'none',
                padding: isScrolled ? '0.85rem 0' : '1.5rem 0',
            }}
        >
            {/* Bottom glow line when scrolled */}
            {isScrolled && (
                <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(59,130,246,0.3), rgba(59,130,246,0.15), transparent)',
                    pointerEvents: 'none'
                }} />
            )}

            <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {/* Logo Area */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    color: isScrolled ? 'var(--accent-slate)' : 'var(--white)',
                    letterSpacing: '-0.02em',
                    transition: 'color 0.3s ease'
                }}>
                    <span style={{
                        color: '#4E9A06',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        fontWeight: 900
                    }}>AUTOMACHINA</span>
                </div>

                {/* Desktop Nav */}
                <div className="desktop-nav" style={{ display: 'none', gap: '2.5rem', alignItems: 'center' }}>
                    {navLinks.map((link) => (
                        <a
                            key={link.name}
                            href={link.href}
                            className="nav-link-hover"
                            style={{
                                color: isScrolled ? 'var(--accent-slate)' : 'var(--white)',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                transition: 'all 0.3s ease',
                                opacity: isScrolled ? 0.8 : 0.9,
                                position: 'relative',
                                paddingBottom: '2px'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.opacity = '1';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.opacity = isScrolled ? '0.8' : '0.9';
                                e.target.style.transform = 'translateY(0)';
                            }}
                        >
                            {link.name}
                        </a>
                    ))}

                    {/* CTA Button */}
                    <a
                        href="#contact"
                        className="nav-cta-btn"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0.5rem 1.25rem',
                            borderRadius: '100px',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                            backgroundColor: isScrolled ? 'var(--accent-slate)' : 'var(--white)',
                            color: isScrolled ? 'var(--white)' : 'var(--accent-slate)',
                            border: 'none',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Get Started
                    </a>
                </div>

                {/* Mobile Menu Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    style={{
                        color: isScrolled || isMobileMenuOpen ? 'var(--accent-slate)' : 'var(--white)',
                        display: 'block',
                        transition: 'color 0.3s ease',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px'
                    }}
                    className="mobile-toggle"
                >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {/* Mobile Nav Overlay */}
            <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.97)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                padding: isMobileMenuOpen ? '3rem 2rem' : '0 2rem',
                maxHeight: isMobileMenuOpen ? '500px' : '0',
                overflow: 'hidden',
                borderBottom: isMobileMenuOpen ? '1px solid rgba(0,0,0,0.06)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: isMobileMenuOpen ? '1.5rem' : '0',
                boxShadow: isMobileMenuOpen ? '0 20px 60px rgba(0,0,0,0.12)' : 'none',
                transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                opacity: isMobileMenuOpen ? 1 : 0,
                pointerEvents: isMobileMenuOpen ? 'auto' : 'none'
            }}>
                {navLinks.map((link) => (
                    <a
                        key={link.name}
                        href={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        style={{
                            color: 'var(--accent-slate)',
                            fontSize: '1.4rem',
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            transition: 'opacity 0.3s ease',
                            opacity: isMobileMenuOpen ? 1 : 0
                        }}
                    >
                        {link.name}
                    </a>
                ))}
                <a
                    href="#contact"
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '0.75rem 2rem',
                        borderRadius: '100px',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        backgroundColor: 'var(--accent-slate)',
                        color: 'var(--white)',
                        textDecoration: 'none',
                        marginTop: '0.5rem',
                        transition: 'all 0.3s ease'
                    }}
                >
                    Get Started
                </a>
            </div>

            <style>{`
                @media (min-width: 768px) {
                    .desktop-nav { display: flex !important; }
                    .mobile-toggle { display: none !important; }
                }
                .nav-link-hover::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 0;
                    height: 1.5px;
                    background: currentColor;
                    transition: width 0.3s ease;
                }
                .nav-link-hover:hover::after {
                    width: 100%;
                }
                .nav-cta-btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 15px rgba(0,0,0,0.15);
                }
            `}</style>
        </nav>
    );
};

export default Navbar;
